import React, {
  useState, useEffect, useCallback, useMemo,
} from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useParams, useHistory } from 'react-router-dom';
import { Download, Shuffle, ShieldCheck } from 'lucide-react';

import { useAuth } from '../../context/AuthContext';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import Button from '../../components/ui/Button';
import DataTable from '../../components/ui/DataTable';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import {
  getRaffle, getRaffleEntries, drawRaffle, exportRaffleWinners,
} from './config';

const Container = styled.div``;

const Card = styled.div`
  background: #fff;
  border: 1px solid #e8ecf0;
  border-radius: 10px;
  padding: 18px;
  margin-bottom: 16px;
`;

const Toolbar = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 16px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  gap: 14px;
`;

const Item = styled.div``;

const ItemLabel = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.dark.little};
  margin-bottom: 4px;
`;

const ItemValue = styled.div`
  font-size: 14px;
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.text.primary};
  word-break: break-all;
`;

const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 12px;
`;

const Hint = styled.div`
  font-size: 12px;
  color: #6c757d;
  margin-top: 10px;
`;

const EmptyBox = styled.div`
  padding: 24px;
  text-align: center;
  color: #6c757d;
  font-size: 13px;
`;

export default function RaffleDetailsPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const history = useHistory();
  const { canWrite } = useAuth();
  const canManage = canWrite('RF');

  const [raffle, setRaffle] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawConfirm, setDrawConfirm] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const [r, e] = await Promise.all([getRaffle(id), getRaffleEntries(id)]);
    setRaffle(r.data?.data || null);
    setEntries(e.data?.data || []);
    setLoading(false);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const handleDraw = useCallback(async () => {
    setBusy(true);
    const res = await drawRaffle(id);
    setBusy(false);
    setDrawConfirm(false);
    if (res?.data?.status) await load();
  }, [id, load]);

  const handleExport = useCallback(async () => {
    const res = await exportRaffleWinners(id);
    if (!res?.data) return;
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${raffle?.title || 'cekilis'}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }, [id, raffle]);

  const columns = useMemo(() => ([
    {
      name: 'winnerRank',
      label: t('raffle.entries.rank'),
      options: { filter: false, sort: true, customBodyRender: (v) => v ?? '-' },
    },
    { name: 'employeeName', label: t('raffle.entries.employee'), options: { filter: false, sort: true } },
    {
      name: 'departmentName',
      label: t('raffle.entries.department'),
      options: { filter: false, sort: true, customBodyRender: (v) => v || '-' },
    },
    {
      name: 'outcome',
      label: t('raffle.entries.outcome'),
      options: {
        filter: false, sort: true,
        customBodyRender: (v) => t(`raffle.outcome.${v}`, { defaultValue: v }),
      },
    },
    {
      name: 'claimStatus',
      label: t('raffle.entries.claimStatus'),
      options: {
        filter: false, sort: true,
        customBodyRender: (v) => t(`raffle.claimStatus.${v}`, { defaultValue: v }),
      },
    },
    {
      name: 'enteredAt',
      label: t('raffle.entries.enteredAt'),
      options: {
        filter: false, sort: true,
        customBodyRender: (v) => (v ? new Date(v).toLocaleString() : '-'),
      },
    },
    {
      name: 'answers',
      label: t('raffle.entries.answers'),
      options: {
        filter: false, sort: false, download: false,
        customBodyRender: (list) => (Array.isArray(list) && list.length > 0
          ? list.map((a) => `${a.questionText}: ${a.answerText}`).join(' | ')
          : '-'),
      },
    },
  ]), [t]);

  if (loading) return <Container><EmptyBox>{t('common.loading')}</EmptyBox></Container>;
  if (!raffle) return <Container><EmptyBox>{t('raffle.notFound')}</EmptyBox></Container>;

  return (
    <Container>
      <Breadcrumbs items={[
        { label: t('raffle.title'), onClick: () => history.push('/raffles') },
        { label: raffle.title },
      ]}
      />

      {canManage && (
        <Toolbar>
          {raffle.isDrawable && (
            <Button data-role="draw-btn" onClick={() => setDrawConfirm(true)}>
              <Shuffle size={15} /> {t('raffle.actions.draw')}
            </Button>
          )}
          {raffle.status === 'drawn' && (
            <Button variant="outline" data-role="export-btn" onClick={handleExport}>
              <Download size={15} /> {t('raffle.actions.exportWinners')}
            </Button>
          )}
        </Toolbar>
      )}

      <Card>
        <Grid>
          <Item>
            <ItemLabel>{t('raffle.fields.status')}</ItemLabel>
            <ItemValue>{t(`raffle.status.${raffle.status}`, { defaultValue: raffle.status })}</ItemValue>
          </Item>
          <Item>
            <ItemLabel>{t('raffle.fields.entryDeadline')}</ItemLabel>
            <ItemValue>{new Date(raffle.entryDeadline).toLocaleDateString()}</ItemValue>
          </Item>
          <Item>
            <ItemLabel>{t('raffle.fields.winnerCount')}</ItemLabel>
            <ItemValue>{raffle.winnerCount} × {raffle.itemsPerWinner}</ItemValue>
          </Item>
          <Item>
            <ItemLabel>{t('raffle.fields.backupWinnerCount')}</ItemLabel>
            <ItemValue>{raffle.backupWinnerCount}</ItemValue>
          </Item>
          <Item>
            <ItemLabel>{t('raffle.fields.entryCount')}</ItemLabel>
            <ItemValue>{raffle.entryCount}</ItemValue>
          </Item>
          <Item>
            <ItemLabel>{t('raffle.fields.claimDeadlineDays')}</ItemLabel>
            <ItemValue>{raffle.claimDeadlineDays ?? '-'}</ItemValue>
          </Item>
        </Grid>
      </Card>

      {raffle.status === 'drawn' && (
        <Card>
          <SectionTitle><ShieldCheck size={15} /> {t('raffle.record.title')}</SectionTitle>
          <Grid>
            <Item>
              <ItemLabel>{t('raffle.record.drawnAt')}</ItemLabel>
              <ItemValue>{new Date(raffle.drawnAt).toLocaleString()}</ItemValue>
            </Item>
            <Item>
              <ItemLabel>{t('raffle.record.participantCount')}</ItemLabel>
              <ItemValue>{raffle.drawParticipantCount}</ItemValue>
            </Item>
            <Item>
              <ItemLabel>{t('raffle.record.excludedCount')}</ItemLabel>
              <ItemValue>{raffle.drawExcludedCount}</ItemValue>
            </Item>
            <Item>
              <ItemLabel>{t('raffle.record.seed')}</ItemLabel>
              <ItemValue data-role="draw-seed">{raffle.drawSeed}</ItemValue>
            </Item>
          </Grid>
          <Hint>{t('raffle.record.hint')}</Hint>
        </Card>
      )}

      <DataTable
        title={t('raffle.entries.title')}
        fetching={false}
        columns={columns}
        data={entries}
        serverSide={false}
        canCreateNewRecord={false}
      />

      <ConfirmDialog
        isOpen={drawConfirm}
        title={t('raffle.confirm.drawTitle')}
        message={t('raffle.confirm.drawMessage', { count: raffle.entryCount })}
        onConfirm={handleDraw}
        onCancel={() => setDrawConfirm(false)}
        loading={busy}
        type="info"
      />
    </Container>
  );
}
